import { Avatar } from '@chakra-ui/react';
import { useWorkspaceLogoSrc } from '../../hooks/workspace/useWorkspaceLogo';
import { getAvatarColor } from '../../utils/avatarColor';

export interface WorkspaceLogoProps {
  workspace: { id: string; name: string; logoHash?: string | null };
  /** Edge of the square, in px. */
  size: number;
  /**
   * Announce the workspace name. Off by default: almost every placement sits
   * next to the name in text, where a second reading is noise.
   */
  labelled?: boolean;
}

/** The monogram's letter height against the tile's edge. */
const MONOGRAM_FONT_RATIO = 0.38;

/**
 * The monogram's letters: the first and last words that start with a letter
 * or digit, so a separator or a parenthetical never becomes one
 * ("Stress Test (permanent)" is ST, not S().
 */
export function workspaceInitials(name: string): string {
  const words = name.split(/\s+/).filter((word) => /^[\p{L}\p{N}]/u.test(word));
  if (words.length === 0) return (name.match(/[\p{L}\p{N}]/u)?.[0] ?? '').toUpperCase();
  // By code point, so a letter outside the BMP is not split in half.
  const first = [...words[0]][0];
  const last = words.length > 1 ? [...words[words.length - 1]][0] : '';
  return `${first}${last}`.toUpperCase();
}

/**
 * A workspace's mark: its uploaded logo, or a monogram (the first letters of
 * its name on a colour picked from its id) when it has none or the image
 * fails. Every surface that names a workspace draws this, so a lab is the
 * same tile in the rail, the switcher and Settings.
 *
 * A rounded square, not a circle: circles are people (`UserDisplay`), so the
 * two never read as one another in the same menu.
 */
export function WorkspaceLogo({ workspace, size, labelled = false }: WorkspaceLogoProps) {
  const src = useWorkspaceLogoSrc(workspace.id, workspace.logoHash);
  const monogram = getAvatarColor(workspace.id);

  return (
    <Avatar
      // Chakra's Avatar sets `data-loaded` on its first image load and never
      // clears it, so a tile reused for another workspace (the rail, after a
      // switch) kept the transparent background and lost its monogram colour.
      // A new workspace or logo is a new Avatar.
      key={`${workspace.id}:${workspace.logoHash ?? ''}`}
      name={workspace.name}
      getInitials={workspaceInitials}
      src={src}
      // The server picks an uploaded logo's tile (`workspace-logo.image.ts`):
      // opaque, or see-through only when the mark reads on both themes, so
      // the image is drawn as stored, on nothing. Until it has loaded (or
      // when it fails) the monogram shows, on its colour.
      bg={monogram.bg}
      color={monogram.color}
      boxSize={`${size}px`}
      flexShrink={0}
      fontWeight="semibold"
      borderRadius={size >= 32 ? 'lg' : 'md'}
      _after={
        src
          ? {
              content: '""',
              position: 'absolute',
              inset: 0,
              borderRadius: 'inherit',
              borderWidth: '1px',
              borderColor: 'logo.tile.ring',
              pointerEvents: 'none',
            }
          : undefined
      }
      sx={{
        // Chakra draws the initials at `--avatar-font-size`, set from the
        // Avatar's own `size` (48px by default), not from `fontSize`; scale
        // it to this tile so the letters keep a margin to its edges.
        '--avatar-font-size': `${Math.round(size * MONOGRAM_FONT_RATIO)}px`,
        img: { borderRadius: 'inherit', objectFit: 'contain' },
        '&[data-loaded]': { bg: 'transparent' },
      }}
      aria-hidden={labelled ? undefined : true}
      data-testid="workspace-logo"
    />
  );
}
